import { UserProfileViewModel, getUserProfileViewModel } from "client/Api/UserProfile/service";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import {
	AlertManager,
	useAlerts,
	useForceRerender
} from "client/Hooks";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { useEffect, useState } from "react";

export type LoadUserProfileViewModelStatus = "ready" | "loading" | "error";

export type LoadUserProfileViewModelHandlers = {
	viewModel: UserProfileViewModel | undefined;
	status: LoadUserProfileViewModelStatus;
	reload(): void;
};

export function useLoadUserProfileViewModel(): LoadUserProfileViewModelHandlers {
	const [status, setStatus] = useState<LoadUserProfileViewModelStatus>("ready");
	const [viewModel, setViewModel] = useState<UserProfileViewModel>();

	const [reload, dataVersion] = useForceRerender();
	const alertManager: AlertManager = useAlerts();

	useEffect(
		() => {
			hasImplicitDependencyOn(dataVersion);

			async function fetchData(signal: AbortSignal): Promise<void> {
				setStatus("loading");

				try {
					const userProfileViewModel: UserProfileViewModel =
						await getUserProfileViewModel(signal)
							.catch(handlePageDataLoadingError);

					setViewModel(userProfileViewModel);

					setStatus("ready");
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_FailedToLoadUserProfileSettingsPleaseTryToRefreshThePage"));
					setStatus("error");
				}
			}

			const cancellation = new AbortController();
			fetchData(cancellation.signal);

			return () => cancellation.abort();
		},
		[alertManager, dataVersion]
	);

	return {
		viewModel,
		reload,
		status
	};
};